import {
  BN,
  compareLowerStr,
  formatUnit,
  isNormalPositive,
  useWallet,
} from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useMemo } from 'react';
import styled from 'styled-components';
import IconInfo from '@/assets/icons/icon-question-20.svg';
import ButtonWithArrow from '@/components/ButtonWithArrow';
import {
  EarnDetailBifi,
  EarnDetailEverdex,
} from '@/components/DashBoard/EarnDetailPanel';
import EarnOverviewPanel from '@/components/DashBoard/EarnOverviewPanel';
import LTVDetailInfo from '@/components/DashBoard/LTVDetailInfo';
import MintedBtcUsdPanel from '@/components/DashBoard/MintedBtcUsdPanel';
import PledgedAssetPanel from '@/components/DashBoard/PledgedAssetPanel';
import ProfitCalculatePanel from '@/components/DashBoard/ProfitCalculatePanel';
import { FlexBox } from '@/components/atom/FlexBox';
import { GlassPanel } from '@/components/atom/GlassPanel';
import { GlowPanel } from '@/components/atom/GlowPanel';
import { StyledText } from '@/components/atom/Text';
import { SectionTitle } from '@/components/atom/Titles';

const DialogAction = dynamic(() => import('@/components/DialogAction'), {
  ssr: false,
});
const RiskDisclaimerDialog = dynamic(
  () => import('@/components/RiskDisclaimerDialog'),
  { ssr: false }
);
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@/components/atom/Tooltip';
import WarnMessage from '@/components/atom/WarnMessage';
import BORROW_APY from '@/configs/borrowApy';
import { MEDIA_QUERY_BREAKPOINT } from '@/configs/mediaQuery';
import { PATH_EARN } from '@/configs/path';
import useBifi from '@/hooks/api/bifi/useBifi';
import useEverdex from '@/hooks/api/everdex/useEverdex';
import useBtcfi from '@/hooks/api/useBtcfi';
import useInitializer from '@/hooks/bifrontSdk/useInitializer';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import useEnv from '@/hooks/useEnv';
import { formatNumber } from '@/libs/formatNumber';
import {
  dashboardCardVariant,
  dashboardContainerVariant,
} from '@/styles/motion';
import { getIsMobile } from '@/utils/mobileDetect';

const DepositContainer = styled(GlassPanel)`
  padding: 64px 24px;
  margin-top: 40px;

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    width: 100%;
  }
`;

const Container = styled(FlexBox)`
  margin-top: 64px;
`;

const StyledGlowPanel = styled(GlowPanel)`
  padding: 1px 2px;
`;

const APYText = styled(StyledText)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 24px;
  gap: 4px;
  background-color: #06092b;

  ${(p) => p.theme.font.black};
  & > strong {
    color: ${(p) => p.theme.colors.green};
    ${(p) => p.theme.text.lg};
    & > span {
      ${(p) => p.theme.text.md};
    }
  }
`;

const CenterText = styled(StyledText)`
  text-align: center;
  white-space: pre-line;
`;

const PositionBox = styled(FlexBox)`
  @media (${MEDIA_QUERY_BREAKPOINT}) {
    & > div {
      width: 468px;
    }
  }
`;

const EarnBox = styled(FlexBox)`
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 1330px) {
    grid-template-columns: 1fr;
  }
`;

const EarnDetailContainer = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template: 'a b';
  grid-template-columns: 1fr 1fr;

  ${(p) => p.theme.gap.xl};
  & > *:nth-child(1) {
    grid-area: a;
  }
  & > *:nth-child(2) {
    grid-area: b;
  }

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    grid-template:
      'a'
      'b';
  }
`;
interface Props {
  isMobile: boolean;
}

export default function Dashboard({ isMobile }: Props) {
  // translation
  const { t } = useTranslation('dashboard');

  // initializer
  const {
    syncedEnv,
    syncedChains,
    syncedBifiHandlers,
    syncedPairs,
    syncedBifi,
    syncedEverdex,
  } = useInitializer();

  // env
  const {
    languageId,
    tokenBifrostBfc,
    tokenBifrostBtcusd,
    tokenBifrostEverdexStakeLp,
  } = useEnv();

  // wallet
  const { account } = useWallet();

  // currency
  const { formatCurrencyValue } = useGetCurrencyString();

  // bifi
  const { isWarningLtv } = useBtcfi();

  // bifi
  const {
    lendingStatuses,
    rewardAmount,
    isLoading: isLoadingRawBifi,
  } = useBifi();

  // everdex
  const {
    lpBalance,
    poolApr,
    depositedLpAmount,
    lpRewardAmount,
    isLoading: isLoadingRawEverdex,
  } = useEverdex();

  // memo
  const synced = useMemo(
    () =>
      syncedEnv &&
      syncedChains &&
      syncedBifiHandlers &&
      syncedPairs &&
      syncedBifi &&
      syncedEverdex,
    [
      syncedEnv,
      syncedChains,
      syncedBifiHandlers,
      syncedPairs,
      syncedBifi,
      syncedEverdex,
    ]
  );
  const isLoadingBifi = useMemo(
    () => !!(account && !synced && isLoadingRawBifi),
    [account, isLoadingRawBifi, synced]
  );
  const isLoadingEverdex = useMemo(
    () => !!(account && !synced && isLoadingRawEverdex),
    [account, isLoadingRawEverdex, synced]
  );
  const isLoading = useMemo(
    () => !!(account && !synced && (isLoadingRawBifi || isLoadingRawEverdex)),
    [account, isLoadingRawBifi, isLoadingRawEverdex, synced]
  );
  const statusBtcusd = useMemo(
    () =>
      lendingStatuses.find((status) =>
        compareLowerStr(status.symbol, tokenBifrostBtcusd?.symbol)
      ),
    [lendingStatuses, tokenBifrostBtcusd?.symbol]
  );
  const pureDepositAmount = useMemo(
    () => statusBtcusd?.deposit?.amount,
    [statusBtcusd?.deposit?.amount]
  );
  const interestAmount = useMemo(
    () => statusBtcusd?.deposit?.interest?.amount,
    [statusBtcusd?.deposit?.interest]
  );
  const depositAmount = useMemo(
    () =>
      formatNumber(
        new BN(
          (isNormalPositive(pureDepositAmount, true) ? pureDepositAmount : 0) ??
            0
        ).plus(
          (isNormalPositive(interestAmount, true) ? interestAmount : 0) ?? 0
        )
      ),
    [interestAmount, pureDepositAmount]
  );
  // amount value
  const depositAmountValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(depositAmount, true) ? depositAmount : '0',
        tokenBifrostBtcusd
      ),
    [depositAmount, formatCurrencyValue, tokenBifrostBtcusd]
  );
  const rewardAmountValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(rewardAmount, true) ? rewardAmount : '0',
        tokenBifrostBtcusd
      ),
    [formatCurrencyValue, rewardAmount, tokenBifrostBtcusd]
  );
  const lpBalanceValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(lpBalance, true) ? lpBalance : '0',
        tokenBifrostBtcusd
      ),
    [lpBalance, formatCurrencyValue, tokenBifrostBtcusd]
  );
  const depositedLpAmountValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(depositedLpAmount, true) ? depositedLpAmount : '0',
        tokenBifrostBtcusd
      ),
    [formatCurrencyValue, depositedLpAmount, tokenBifrostBtcusd]
  );
  const lpRewardAmountValue = useMemo(
    () =>
      formatCurrencyValue(
        isNormalPositive(lpRewardAmount, true) ? lpRewardAmount : '0',
        tokenBifrostBfc
      ),
    [formatCurrencyValue, lpRewardAmount, tokenBifrostBfc]
  );
  // total amount value
  const totalDepositAmountValue = useMemo(
    () =>
      formatNumber(
        new BN(depositAmountValue)
          .plus(lpBalanceValue)
          .plus(depositedLpAmountValue)
      ),
    [lpBalanceValue, depositAmountValue, depositedLpAmountValue]
  );
  const willEarnAmountValue = useMemo(
    () => formatNumber(new BN(rewardAmountValue).plus(lpRewardAmountValue)),
    [rewardAmountValue, lpRewardAmountValue]
  );
  // is using
  const isUsingBifi = useMemo(
    () =>
      (isNormalPositive(depositAmount, true) &&
        new BN(depositAmount ?? 0).gt(
          formatUnit(10, tokenBifrostBtcusd?.decimals ?? 18)
        )) ||
      (isNormalPositive(rewardAmount, true) &&
        new BN(rewardAmount ?? 0).gt(
          formatUnit(10, tokenBifrostBtcusd?.decimals ?? 18)
        )),
    [depositAmount, rewardAmount, tokenBifrostBtcusd?.decimals]
  );
  const isUsingEverdex = useMemo(
    () =>
      (isNormalPositive(lpBalance, true) &&
        new BN(lpBalance ?? 0).gt(
          formatUnit(10, tokenBifrostEverdexStakeLp?.decimals ?? 18)
        )) ||
      (isNormalPositive(depositedLpAmount, true) &&
        new BN(depositedLpAmount ?? 0).gt(
          formatUnit(10, tokenBifrostEverdexStakeLp?.decimals ?? 18)
        )),
    [lpBalance, tokenBifrostEverdexStakeLp?.decimals, depositedLpAmount]
  );
  const isUsingEarn = useMemo(
    () => isUsingBifi || isUsingEverdex,
    [isUsingBifi, isUsingEverdex]
  );

  return (
    <>
      <Container
        as={motion.div}
        variants={dashboardContainerVariant}
        initial="hidden"
        animate="show"
        exit="exit"
        $flex
        $direction="column"
        $gap={80}
      >
        <FlexBox
          as={motion.div}
          variants={dashboardContainerVariant}
          initial="hidden"
          animate="show"
          $flex
          $direction="column"
          $gap="xl"
        >
          <FlexBox $flex $gap="xxs">
            <SectionTitle>{t('myPosition')}</SectionTitle>
            <StyledGlowPanel
              $rounded="xxl"
              $padding={{
                x: 'md',
                y: 'xxs',
              }}
            >
              <APYText>
                <Trans
                  t={t}
                  i18nKey="borrowApy"
                  components={[<strong key="apy-1" />, <span key="apy-2" />]}
                  values={{
                    apy: BORROW_APY,
                  }}
                />
              </APYText>
            </StyledGlowPanel>
          </FlexBox>
          {isWarningLtv ? (
            <FlexBox $flex>
              <Tooltip placement="right-start" offset={14}>
                <TooltipTrigger>
                  <WarnMessage padding={{ x: 'md', y: 'xs' }} rounded="md">
                    <IconInfo />
                    <StyledText $fontSize="md">{t('warnLtv')}</StyledText>
                  </WarnMessage>
                </TooltipTrigger>
                <TooltipContent
                  $padding={{ y: 'xs', x: 'md' }}
                  style={{ width: languageId === 'ko' ? '305px' : '345px' }}
                >
                  <TooltipArrow />
                  <LTVDetailInfo />
                </TooltipContent>
              </Tooltip>
            </FlexBox>
          ) : (
            ''
          )}
          <PositionBox $flex $gap="xl">
            <MintedBtcUsdPanel isMobile={isMobile} />
            <PledgedAssetPanel isMobile={isMobile} />
          </PositionBox>
        </FlexBox>
        <FlexBox
          as={motion.div}
          variants={dashboardContainerVariant}
          initial="hidden"
          animate="show"
          $flex
          $direction="column"
          $gap="xl"
        >
          <FlexBox $flex $gap="xxs">
            <SectionTitle>{t('earnStatus')}</SectionTitle>
          </FlexBox>
          <EarnBox $flex $gap="xl">
            <EarnOverviewPanel
              depositAmountValue={totalDepositAmountValue}
              willEarnAmountValue={willEarnAmountValue}
              isLoading={isLoading}
            />
            <ProfitCalculatePanel />
          </EarnBox>
          {account ? (
            isUsingEarn ? (
              <EarnDetailContainer>
                {isUsingEverdex && (
                  <EarnDetailEverdex
                    amount={lpBalance}
                    apr={poolApr}
                    stakeAmount={depositedLpAmount}
                    rewardAmount={lpRewardAmount}
                    isLoading={isLoadingEverdex}
                  />
                )}
                {isUsingBifi && (
                  <EarnDetailBifi
                    amount={pureDepositAmount}
                    interestAmount={interestAmount}
                    rewardAmount={rewardAmount}
                    isLoading={isLoadingBifi}
                  />
                )}
              </EarnDetailContainer>
            ) : (
              <DepositContainer
                as={motion.div}
                variants={dashboardCardVariant}
                $rounded="xxl"
                $flex
                $direction="column"
                $gap="xl"
                $padding="xl"
                $justify="center"
                $align="center"
              >
                <CenterText $fontSize="xl" $fontWeight="bold">
                  {t('depositNow')}
                </CenterText>
                <Link href={PATH_EARN}>
                  <ButtonWithArrow
                    rounded="lg"
                    padding={{
                      x: 'xl',
                      y: 'md',
                    }}
                    variant="primary"
                  >
                    {t('goToDeposit')}
                  </ButtonWithArrow>
                </Link>
              </DepositContainer>
            )
          ) : null}
        </FlexBox>
      </Container>
      <DialogAction />
      <RiskDisclaimerDialog />
    </>
  );
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const isMobile = getIsMobile(context);

  return {
    props: {
      ...(await serverSideTranslations(context.locale as string, [
        'common',
        'tooltip',
        'dialog',
        'dashboard',
      ])),
      isMobile,
    },
  };
};
